/**
 * 完成确认（docs/成就系统.md「完成确认」）：孩子做完学习任务、点日程 / 提醒的"做完了"后在这里等家长确认。
 * 确认时可以改分数（学习任务另加按时奖励），"不算"不加分。首页和时间表共用。
 */
import { BookOpen, Bell, CalendarCheck, Check, WalletCards } from "lucide-react";
import { useState } from "react";
import { run, shallowEqual, useCalendar, useStore, writeEntity, type Checkin, type DataState } from "../core";
import { Button, NumberStepper } from "../ui";
import { GrantCardSheet } from "./cards/GrantCardSheet";
import "./CheckinsCard.css";

/** 待确认的记录，早做完的在前；传 childId 时只看这个孩子 */
export function pendingCheckinsOf(s: DataState, childId?: string | null): Checkin[] {
  return Object.values(s.checkins)
    .filter((k) => k.status === "pending" && !k.deletedAt && (childId == null || k.childId === childId))
    .sort((a, b) => a.doneAt - b.doneAt);
}

const SOURCE_TEXT: Record<string, string> = { event: "日程", reminder: "提醒", assignment: "学习任务" };

export async function decideCheckin(ck: Checkin, approve: boolean, points?: number): Promise<boolean> {
  const body = approve ? { decision: "approve", points: points ?? ck.points } : { decision: "reject" };
  const total = (points ?? ck.points) + (ck.bonusPoints ?? 0);
  const ok = await run(() => writeEntity("checkins", "POST", `checkins/${ck.id}/decide`, body).then(() => true), approve ? `已确认，加 ${total} 分` : "已标记为不算");
  return ok === true;
}

export function CheckinRow({ ck }: { ck: Checkin }) {
  const cal = useCalendar();
  const [points, setPoints] = useState(ck.points);
  const [busy, setBusy] = useState(false);
  const [granting, setGranting] = useState(false);
  const child = useStore((s) => s.children[ck.childId] ?? null);
  const Icon = ck.sourceType === "assignment" ? BookOpen : ck.sourceType === "reminder" ? Bell : CalendarCheck;
  const day = cal.relativeDay(cal.dateString(ck.doneAt));
  const decide = async (approve: boolean) => {
    setBusy(true);
    await decideCheckin(ck, approve, points);
    setBusy(false);
  };
  return (
    <div className="checkin-row">
      <div className="checkin-info">
        <span className={`checkin-icon is-${ck.sourceType}`}>
          <Icon size={18} strokeWidth={2.4} />
        </span>
        <div>
          <strong>{ck.title || SOURCE_TEXT[ck.sourceType] || "做完了"}</strong>
          <small>
            {SOURCE_TEXT[ck.sourceType] ?? ""} · {day} {cal.timeString(ck.doneAt)} 做完
            {ck.bonusPoints > 0 ? ` · 按时完成 +${ck.bonusPoints}` : ""}
          </small>
        </div>
      </div>
      <div className="checkin-actions">
        <NumberStepper value={points} min={0} max={200} onChange={setPoints} />
        <Button variant="soft" height={40} disabled={busy} onClick={() => void decide(false)} className="checkin-reject">
          不算
        </Button>
        {/* 确认的同时发一张卡（卡记下这次完成） */}
        <Button variant="soft" height={40} disabled={busy} onClick={() => setGranting(true)} aria-label="确认并发卡" title="确认并发卡">
          <WalletCards size={16} strokeWidth={2.4} />
        </Button>
        <Button variant="dark" height={40} busy={busy} onClick={() => void decide(true)}>
          确认 +{points + (ck.bonusPoints ?? 0)}
        </Button>
      </div>
      <GrantCardSheet open={granting} onClose={() => setGranting(false)} child={child} checkin={ck} approvePoints={points} />
    </div>
  );
}

/** 首页：当前孩子待确认的事 */
export function CheckinsCard({ childId }: { childId: string }) {
  const list = useStore((s) => pendingCheckinsOf(s, childId), shallowEqual);
  const [busy, setBusy] = useState(false);
  if (list.length === 0) return null;
  const approveAll = async () => {
    setBusy(true);
    for (const ck of list) await decideCheckin(ck, true);
    setBusy(false);
  };
  return (
    <section className="home-card checkins-card" aria-label="待确认">
      <div className="home-card-head">
        <h2>
          做完了，待确认 <span className="kp-num">{list.length}</span>
        </h2>
        {list.length > 1 && (
          <Button variant="soft" height={34} busy={busy} onClick={() => void approveAll()}>
            <Check size={16} strokeWidth={2.6} /> 全部确认
          </Button>
        )}
      </div>
      {list.map((ck) => (
        <CheckinRow key={ck.id} ck={ck} />
      ))}
    </section>
  );
}
